/**
 * @mfa/theme — the single source of visual truth for Meal4ALL.
 *
 * Consumed by `client-app` (iOS, Android, Web) and `admin-portal`. Nothing in
 * either app may declare a colour, font size, radius, shadow or spacing value
 * of its own; if a value is missing, add it here.
 *
 * Layers, innermost first:
 *   palette.ts     seed colours — the only raw hex in the repo
 *   alpha.ts       the opacity ladder and compositing helpers
 *   semantic.ts    roles (text.secondary, status.progress) built from the above
 *   typography.ts  the type scale and composed text roles
 *   layout.ts      spacing, radii, elevation, sizing, breakpoints, motion
 *   css.ts         emits the same tokens as CSS custom properties for the web
 */

export { alpha, withAlpha, flatten, hexToRgb, rgbToHex, contrastRatio, luminance, readableOn } from './alpha';
export type { AlphaToken, AlphaValue } from './alpha';

export { seed, brandFixed } from './palette';
export type { SeedToken } from './palette';

export { colors, statusTone } from './semantic';
export type { Colors, StatusTone, ToneColors } from './semantic';

export {
  fontFamily,
  fontSize,
  fontWeight,
  letterSpacing,
  lineHeight,
  textStyles,
} from './typography';
export type { TextRole, TextStyle, FontWeight } from './typography';

export {
  space,
  radius,
  elevation,
  sizing,
  breakpoints,
  mediaQuery,
  motion,
  zIndex,
} from './layout';
export type { SpaceToken, ElevationToken, Breakpoint, Elevation } from './layout';

export { cssVariables, cssVar, tokenMap, textRoleClasses } from './css';

import { colors } from './semantic';
import { elevation, motion, radius, sizing, space, zIndex, breakpoints } from './layout';
import { fontFamily, fontSize, fontWeight, textStyles } from './typography';

/**
 * The whole theme as one object, for context providers and styled-system
 * adapters. Prefer importing the specific slice you need.
 */
export const theme = {
  colors,
  space,
  radius,
  elevation,
  sizing,
  breakpoints,
  motion,
  zIndex,
  fontFamily,
  fontSize,
  fontWeight,
  textStyles,
} as const;

export type Theme = typeof theme;
